// The layout of the chain: the drawn chain is turned into the input of the
// layered layout engine (one rank per column, the root at rank 0, each card
// with its size), and the engine's output into card positions on the canvas.
// Pure: the mapping is tested without a canvas.
//
// Both directions are two layouts glued at the root: the columns of the
// hosts side take negative ranks (leftwards), those of the parts side
// positive ones (rightwards), and the root's rank 0 is shared.

import {
  EdgeLabel,
  Graph,
  GraphLabel,
  NodeLabel,
  layout,
} from "@dagrejs/dagre";
import { Edge, Node } from "@xyflow/react";

import { ChainCard } from "../../types/valueChain";
import type { Subsystem, TreeCardInfo } from "./treeModel";
import {
  CollapsedRow,
  DrawnEdge,
  FocusSummary,
  Highlight,
  PathColour,
  VisibleChain,
  VisibleColumn,
  cardName,
  focusSummary,
  furthestHostRank,
  neighboursOf,
  rowsOf,
} from "./chainModel";

export interface Size {
  width: number;
  height: number;
}

export interface Position {
  x: number;
  y: number;
}

// One card as the engine takes it
export interface LayoutNode extends Size {
  id: string;
  // Its column: the root's is 0
  rank: number;
}

export interface LayoutEdge {
  source: string;
  target: string;
}

export interface LayoutInput {
  nodes: LayoutNode[];
  // The edges that run to a later column: the engine orders the cards of a
  // column by them
  edges: LayoutEdge[];
}

// What a card node carries: a card of the chain, the row that stands for the
// cards of a column not shown, the box of an area, or, in the tree view, a
// closed subsystem or the box of the open one. `dimmed` is set by a maker
// highlight that does not keep the node lit. A node of the tree of hosts is
// drawn mirrored: the tree grows leftwards, so its edges leave it on the left
// and come into it on the right.
export type ChainNodeData = { mirrored?: boolean } & (
  | {
      variant: "card";
      card: ChainCard;
      root: boolean;
      dimmed: boolean;
      // Its own colour, when it is an intermediate module of the hosts side
      pathColour: PathColour | null;
      // The colour of each intermediate module, for the card's path rows
      pathColours: ReadonlyMap<string, PathColour>;
      // The names of the cards it is stated with nearer the root
      neighbours: string[];
      // A host in the furthest column of the focus view: drawn compact,
      // its name and its paths
      compact: boolean;
      // What the focus card says of its hosts; null for any other card
      summary: FocusSummary | null;
      // The kinds of part its drawn edges run into, for an opened card
      partsDrawn: number;
      // Its place in the tree view; absent in the other views
      tree?: TreeCardInfo;
    }
  | {
      variant: "collapsed";
      row: CollapsedRow;
      dimmed: boolean;
      // The name of the root, which the parts stub is titled by
      rootName: string;
    }
  | {
      variant: "area";
      area: AreaBox;
      // Never dimmed: it is drawn behind its cards
      dimmed: false;
    }
  | {
      variant: "subsystem";
      subsystem: Subsystem;
      dimmed: boolean;
    }
  | {
      variant: "branch";
      subsystem: Subsystem;
      width: number;
      height: number;
      // Never dimmed: it is drawn behind its cards
      dimmed: false;
    }
);

// How the tier views arrange a column's areas: each column stacking its own,
// or each area a band across the columns
export type Arrangement = "grouped" | "matrix";

// The box drawn behind the cards of one area in one column
export interface AreaBox extends Position, Size {
  id: string;
  name: string;
  nature: string | null;
  // Its kinds in the column, those in its row included
  kinds: number;
}

export type ChainNode = Node<ChainNodeData, "chainCard">;

// How an edge is drawn: straight for a direct one, stepped otherwise; in the
// tree view, the first level of the open subsystem hangs on a rail down its
// box
export type EdgeShape = "straight" | "stepped" | "rail";

// What an edge's colour says, the first that holds: left out of the read to
// break a cycle, suspect, resting on typical-host rows alone, direct, on an
// intermediate module's paths, or none of these. In the tree view, an edge
// on the trail of open kinds, and another host of a kind in its subsystem.
export type EdgeTone =
  | "broken"
  | "suspect"
  | "typical"
  | "direct"
  | "path"
  | "open"
  | "cross"
  | "plain";

export type ChainEdgeData = {
  drawn: DrawnEdge;
  shape: EdgeShape;
  tone: EdgeTone;
  // The colour of the module whose paths it is on
  pathColour: PathColour | null;
  dimmed: boolean;
  // The host is at the edge's target, not its source: in the tree of what
  // the root is built into, where each host hangs on the part it holds
  hostAtTarget?: boolean;
};

export type ChainFlowEdge = Edge<ChainEdgeData, "chainEdge">;

// The head of a column, the root's and the parts stub's included: all on one
// line above the highest card, as wide as the column
export interface ColumnHead extends Position {
  // The column's rank; the root's is 0
  rank: number;
  width: number;
  // Its words
  label: string;
  // The focus's head, drawn in the accent
  accent: boolean;
}

// The size of a card not measured yet
export const CARD_SIZE: Size = { width: 300, height: 280 };

// The size of a compact host card of the focus view
export const COMPACT_SIZE: Size = { width: 220, height: 56 };

// The size of a collapsed row not measured yet
export const COLLAPSED_SIZE: Size = { width: 300, height: 40 };

// Space between the cards of a column, and between columns
const CARD_GAP = 24;
const COLUMN_GAP = 96;

// Space between the line of the heads and the highest card
const HEAD_GAP = 36;

// Whether a column's cards are drawn compact: the furthest column of the
// hosts side, in the focus view
const compactColumn = (
  visible: VisibleChain,
  column: VisibleColumn,
): boolean =>
  visible.direction === "both" &&
  column.side === "hosts" &&
  column.rank === furthestHostRank(visible);

// The drawn chain as the engine's input: every card and collapsed row with
// its rank and size, measured where it has been, the default otherwise
export const toLayoutInput = (
  visible: VisibleChain,
  measured: ReadonlyMap<string, Size>,
): LayoutInput => {
  const nodes: LayoutNode[] = [
    { id: visible.root.id, rank: 0, ...(measured.get(visible.root.id) ?? CARD_SIZE) },
  ];
  for (const column of visible.columns) {
    const size = compactColumn(visible, column) ? COMPACT_SIZE : CARD_SIZE;
    for (const card of column.cards) {
      nodes.push({
        id: card.id,
        rank: column.rank,
        ...(measured.get(card.id) ?? size),
      });
    }
    for (const row of rowsOf(column)) {
      nodes.push({
        id: row.id,
        rank: column.rank,
        ...(measured.get(row.id) ?? COLLAPSED_SIZE),
      });
    }
  }
  const rank = new Map(nodes.map((node) => [node.id, node.rank]));
  const edges = visible.edges
    .filter(
      (edge) =>
        (rank.get(edge.source) ?? 0) < (rank.get(edge.target) ?? 0),
    )
    .map((edge) => ({ source: edge.source, target: edge.target }));
  return { nodes, edges };
};

// Runs the engine: the top-left corner of each card, left to right by rank
export const runLayout = (input: LayoutInput): Map<string, Position> => {
  const graph = new Graph<GraphLabel, NodeLabel, EdgeLabel>();
  // The hosts side's ranks are negative: the engine is given them shifted so
  // that the leftmost column is at 0
  const lowest = Math.min(0, ...input.nodes.map((node) => node.rank));
  const ranks = new Map(
    input.nodes.map((node) => [node.id, node.rank - lowest]),
  );
  // The ranks are the columns of the response; the engine is not asked to
  // find them. It works on doubled ranks (a rank between two columns makes
  // room for edge labels, and halves the space between ranks), so a column
  // is put at twice its rank. A node of its own (the root of its nesting
  // graph) is put before the first.
  const assignRanks = (
    ranked: Graph<GraphLabel, NodeLabel, EdgeLabel>,
  ): void => {
    for (const id of ranked.nodes()) {
      const rank = ranks.get(id);
      ranked.node(id).rank = rank === undefined ? -1 : rank * 2;
    }
  };
  const label: GraphLabel = {
    rankdir: "LR",
    nodesep: CARD_GAP,
    ranksep: COLUMN_GAP,
    marginx: 0,
    marginy: 0,
  };
  graph.setGraph(Object.assign(label, { ranker: assignRanks }));
  graph.setDefaultEdgeLabel(() => ({}));
  for (const node of input.nodes) {
    graph.setNode(node.id, {
      width: node.width,
      height: node.height,
      rank: ranks.get(node.id),
    });
  }
  for (const edge of input.edges) {
    graph.setEdge(edge.source, edge.target);
  }
  layout(graph);

  const positions = new Map<string, Position>();
  for (const node of input.nodes) {
    const placed = graph.node(node.id);
    positions.set(node.id, {
      x: (placed.x ?? 0) - node.width / 2,
      y: (placed.y ?? 0) - node.height / 2,
    });
  }
  return positions;
};

// The kinds of part a card's drawn edges run into, a collapsed row's cards
// each counted, a broken edge not
export const partsDrawn = (visible: VisibleChain, id: string): number =>
  new Set(
    visible.edges
      .filter((edge) => !edge.broken)
      .flatMap((edge) => edge.edges)
      .filter((edge) => edge.host === id)
      .map((edge) => edge.part),
  ).size;

// The cards of the drawn chain at their positions, as the canvas takes them.
// A card without a position is left out; with a highlight, a card it does
// not keep lit is dimmed.
export const placeCards = (
  visible: VisibleChain,
  positions: ReadonlyMap<string, Position>,
  highlight: Highlight | null = null,
): ChainNode[] => {
  const nodes: ChainNode[] = [];
  const dimmed = (id: string) => highlight !== null && !highlight.cards.has(id);
  const place = (id: string, data: ChainNodeData) => {
    const position = positions.get(id);
    if (position) {
      nodes.push({ id, type: "chainCard", position, data });
    }
  };
  const cardData = (
    card: ChainCard,
    root: boolean,
    compact: boolean,
  ): ChainNodeData => ({
    variant: "card",
    card,
    root,
    dimmed: dimmed(card.id),
    pathColour: visible.pathColours.get(card.id) ?? null,
    pathColours: visible.pathColours,
    neighbours: root ? [] : neighboursOf(visible, card).map(cardName),
    compact,
    summary: root ? focusSummary(visible) : null,
    partsDrawn: partsDrawn(visible, card.id),
  });
  place(visible.root.id, cardData(visible.root, true, false));
  for (const column of visible.columns) {
    const compact = compactColumn(visible, column);
    for (const card of column.cards) {
      place(card.id, cardData(card, false, compact));
    }
    for (const row of rowsOf(column)) {
      place(row.id, {
        variant: "collapsed",
        row,
        dimmed: highlight !== null,
        rootName: cardName(visible.root),
      });
    }
  }
  return nodes;
};

// The boxes of the areas as nodes, drawn behind the cards
export const placeAreas = (areas: readonly AreaBox[]): ChainNode[] =>
  areas.map((area) => ({
    id: area.id,
    type: "chainCard",
    position: { x: area.x, y: area.y },
    width: area.width,
    height: area.height,
    zIndex: -1,
    selectable: false,
    focusable: false,
    data: { variant: "area", area, dimmed: false },
  }));

// The space a tier view keeps around the cards of an area, above them for
// the area's name, and between two areas of a column
export const AREA_PAD = 12;
export const AREA_HEAD = 30;
const AREA_GAP = 18;

// The space between two columns of a tier view: wider than the engine's, so
// that the boxes stand apart
const TIER_COLUMN_GAP = 110;

// The cards of a tier view at their positions, and the boxes of its areas.
// Each column is a stack: grouped, its areas one under the other in their
// order; as a matrix, each area in a band of its own across the columns, as
// tall as its tallest cell, so an area reads along one line. The root's
// column holds the root, and a class opened beside it, alone.
export const tierLayout = (
  visible: VisibleChain,
  measured: ReadonlyMap<string, Size>,
  arrangement: Arrangement,
): { positions: Map<string, Position>; areas: AreaBox[] } => {
  const sizes = new Map(
    toLayoutInput(visible, measured).nodes.map((node) => [node.id, node]),
  );
  const heightOf = (id: string) => sizes.get(id)?.height ?? CARD_SIZE.height;
  const widthOf = (id: string) => sizes.get(id)?.width ?? CARD_SIZE.width;
  const idsOf = (column: VisibleColumn): string[] => [
    ...column.cards.map((card) => card.id),
    ...rowsOf(column).map((row) => row.id),
  ];
  const stackHeight = (ids: readonly string[]): number =>
    ids.reduce((total, id) => total + heightOf(id), 0) +
    Math.max(ids.length - 1, 0) * CARD_GAP;

  const rootIds = [
    visible.root.id,
    ...visible.columns.filter((column) => column.rank === 0).flatMap(idsOf),
  ];
  const ranks = Array.from(
    new Set([0, ...visible.columns.map((column) => column.rank)]),
  ).sort((a, b) => a - b);
  const columnIds = new Map<number, string[]>(
    ranks.map((rank) => [
      rank,
      rank === 0
        ? rootIds
        : visible.columns.filter((column) => column.rank === rank).flatMap(idsOf),
    ]),
  );

  // Each column's left edge, its boxes as wide as its widest card
  const left = new Map<number, number>();
  const width = new Map<number, number>();
  let x = 0;
  for (const rank of ranks) {
    const ids = columnIds.get(rank) ?? [];
    const widest = Math.max(CARD_SIZE.width, ...ids.map(widthOf));
    left.set(rank, x + AREA_PAD);
    width.set(rank, widest);
    x += widest + 2 * AREA_PAD + TIER_COLUMN_GAP;
  }

  const positions = new Map<string, Position>();
  const stack = (ids: readonly string[], rank: number, top: number) => {
    let y = top;
    for (const id of ids) {
      positions.set(id, { x: left.get(rank)!, y });
      y += heightOf(id) + CARD_GAP;
    }
  };
  stack(rootIds, 0, AREA_HEAD);

  const areas: AreaBox[] = [];
  const box = (
    rank: number,
    key: string,
    group: { name: string; nature: string | null; kinds: number },
    top: number,
    height: number,
  ) =>
    areas.push({
      id: `area:${rank}:${key}`,
      name: group.name,
      nature: group.nature,
      kinds: group.kinds,
      x: left.get(rank)! - AREA_PAD,
      y: top,
      width: width.get(rank)! + 2 * AREA_PAD,
      height,
    });
  const groupIds = (group: VisibleColumn["groups"][number]): string[] => [
    ...group.cards.map((card) => card.id),
    ...(group.collapsed ? [group.collapsed.id] : []),
  ];
  const kindsOf = (group: VisibleColumn["groups"][number]): number =>
    group.cards.length + (group.collapsed?.cards.length ?? 0);
  const boxHeight = (ids: readonly string[]) =>
    AREA_HEAD + stackHeight(ids) + AREA_PAD;

  // A column's own row, for the areas it does not show, goes under them
  const placeRow = (column: VisibleColumn, top: number) => {
    if (column.collapsed) {
      positions.set(column.collapsed.id, { x: left.get(column.rank)!, y: top });
    }
  };

  const tierColumns = visible.columns.filter((column) => column.rank !== 0);
  if (arrangement === "grouped") {
    for (const column of tierColumns) {
      if (column.groups.length === 0) {
        stack(idsOf(column), column.rank, AREA_HEAD);
        continue;
      }
      let top = 0;
      for (const group of column.groups) {
        const ids = groupIds(group);
        stack(ids, column.rank, top + AREA_HEAD);
        const height = boxHeight(ids);
        box(column.rank, group.key, { ...group, kinds: kindsOf(group) }, top, height);
        top += height + AREA_GAP;
      }
      placeRow(column, top);
    }
    return { positions, areas };
  }

  // The matrix: one band per area, in the areas' order, as tall as its
  // tallest cell
  const bands = new Map<string, { order: number; height: number }>();
  for (const column of tierColumns) {
    for (const group of column.groups) {
      const height = boxHeight(groupIds(group));
      const band = bands.get(group.key);
      bands.set(group.key, {
        order: group.order,
        height: Math.max(band?.height ?? 0, height),
      });
    }
  }
  const bandTop = new Map<string, number>();
  let top = 0;
  for (const [key, band] of Array.from(bands.entries()).sort(
    ([, a], [, b]) => a.order - b.order,
  )) {
    bandTop.set(key, top);
    top += band.height + AREA_GAP;
  }
  for (const column of tierColumns) {
    if (column.groups.length === 0) {
      stack(idsOf(column), column.rank, AREA_HEAD);
      continue;
    }
    for (const group of column.groups) {
      const at = bandTop.get(group.key)!;
      stack(groupIds(group), column.rank, at + AREA_HEAD);
      box(
        column.rank,
        group.key,
        { ...group, kinds: kindsOf(group) },
        at,
        bands.get(group.key)!.height,
      );
    }
    // Under the last band
    placeRow(column, top);
  }
  return { positions, areas };
};

// How a drawn edge is drawn: a direct one straight, across the columns it
// spans, the others stepped
export const edgeShape = (drawn: DrawnEdge): EdgeShape =>
  drawn.direct ? "straight" : "stepped";

// What a drawn edge's colour says
export const edgeTone = (drawn: DrawnEdge): EdgeTone => {
  if (drawn.broken) {
    return "broken";
  }
  if (drawn.suspect) {
    return "suspect";
  }
  if (drawn.typical) {
    return "typical";
  }
  if (drawn.direct) {
    return "direct";
  }
  return drawn.pathModule ? "path" : "plain";
};

// The edges of the drawn chain, as the canvas takes them; with a highlight,
// an edge it does not keep lit is dimmed. Without `skipEdges`, an edge that
// skips a column is left out.
export const toFlowEdges = (
  visible: VisibleChain,
  highlight: Highlight | null = null,
  skipEdges: boolean = true,
): ChainFlowEdge[] =>
  visible.edges
    .filter((drawn) => skipEdges || !drawn.direct)
    .map((drawn) => ({
      id: drawn.id,
      source: drawn.source,
      target: drawn.target,
      type: "chainEdge",
      data: {
        drawn,
        shape: edgeShape(drawn),
        tone: edgeTone(drawn),
        pathColour: drawn.pathModule
          ? (visible.pathColours.get(drawn.pathModule) ?? null)
          : null,
        dimmed: highlight !== null && !highlight.edges.has(drawn.id),
      },
    }));

// The natures that take no plural
const MASS_NATURES = new Set([
  "equipment",
  "software",
  "hardware",
  "infrastructure",
  "materiel",
]);

// A nature as a column's head says it: "modules", "equipment"
export const pluralNature = (nature: string): string =>
  MASS_NATURES.has(nature) || nature.endsWith("s")
    ? nature
    : nature.endsWith("y") && !/[aeiou]y$/.test(nature)
      ? `${nature.slice(0, -1)}ies`
      : `${nature}s`;

// The words of a column's head: its steps from the root, in for the parts,
// away for the hosts, and what its kinds hold, "mostly" when not all do
export const headLabel = (
  column: Pick<VisibleColumn, "step" | "side" | "nature" | "mixed">,
): string => {
  const steps = `${column.step} step${column.step === 1 ? "" : "s"} ${
    column.side === "hosts" ? "away" : "in"
  }`;
  if (!column.nature) {
    return steps;
  }
  return `${steps} · ${column.mixed ? "mostly " : ""}${pluralNature(column.nature)}`;
};

// The words of the root's head: what it holds, or the focus of a chain read
// both ways
export const rootHeadLabel = (visible: VisibleChain): string => {
  if (visible.direction === "both") {
    return "Focus";
  }
  const nature = visible.root.nature ?? "root";
  return nature.charAt(0).toUpperCase() + nature.slice(1);
};

// The words of the parts stub's head
export const STUB_HEAD = "Parts · optional";

// The head of the root's column and of each column with a placed card, all
// on one line above the highest card, each as wide as its column
export const columnHeads = (
  visible: VisibleChain,
  positions: ReadonlyMap<string, Position>,
  measured: ReadonlyMap<string, Size> = new Map(),
  // Further up still: above the boxes of a tier view's areas
  lift: number = 0,
): ColumnHead[] => {
  const sizes = new Map(
    toLayoutInput(visible, measured).nodes.map((node) => [node.id, node]),
  );
  const idsOf = (column: VisibleColumn): string[] => [
    ...column.cards.map((card) => card.id),
    ...rowsOf(column).map((row) => row.id),
  ];
  // A class opened beside the root is drawn in the root's column
  const columns: { rank: number; ids: string[]; label: string; accent: boolean }[] = [
    {
      rank: 0,
      ids: [
        visible.root.id,
        ...visible.columns
          .filter((column) => column.rank === 0)
          .flatMap(idsOf),
      ],
      label: rootHeadLabel(visible),
      accent: visible.direction === "both",
    },
    ...visible.columns
      .filter((column) => column.rank !== 0)
      .map((column) => ({
        rank: column.rank,
        ids: idsOf(column),
        label: column.collapsed?.stub ? STUB_HEAD : headLabel(column),
        accent: false,
      })),
  ];
  const placed = columns
    .map((column) => ({
      ...column,
      ids: column.ids.filter((id) => positions.has(id)),
    }))
    .filter((column) => column.ids.length > 0);
  if (placed.length === 0) {
    return [];
  }
  const top = Math.min(
    ...placed.flatMap((column) => column.ids.map((id) => positions.get(id)!.y)),
  );
  return placed.map((column) => ({
    rank: column.rank,
    label: column.label,
    accent: column.accent,
    x: Math.min(...column.ids.map((id) => positions.get(id)!.x)),
    y: top - HEAD_GAP - lift,
    width: Math.max(
      ...column.ids.map((id) => sizes.get(id)?.width ?? CARD_SIZE.width),
    ),
  }));
};
